/**
 * @module @repo/global-login-sdk/react
 * Router-agnostische React-Bindings: Provider, Session-Hook und Guards.
 * Bewusst ohne Router-Abhängigkeit, damit das SDK in jede App passt
 * (TanStack Router, react-router, …).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient, Session } from '@supabase/supabase-js';
import type { GlobalLoginConfig } from './config';
import { roleForApp, hasAppAccess, readClaims } from './claims';
import { DEFAULT_COOKIE_DOMAIN } from './config';
import { redirectToLogin, loginPopup } from './redirect';

interface GlobalLoginContextValue {
  client: SupabaseClient;
  config: GlobalLoginConfig;
}

const GlobalLoginContext = createContext<GlobalLoginContextValue | null>(null);

/** Stellt Client + Config für die SDK-Hooks bereit. */
export function GlobalLoginProvider(props: {
  client: SupabaseClient;
  config: GlobalLoginConfig;
  children: ReactNode;
}): ReactNode {
  const { client, config, children } = props;
  return (
    <GlobalLoginContext.Provider value={{ client, config }}>{children}</GlobalLoginContext.Provider>
  );
}

function useCtx(): GlobalLoginContextValue {
  const ctx = useContext(GlobalLoginContext);
  if (!ctx) throw new Error('useGlobalLogin muss innerhalb von <GlobalLoginProvider> stehen.');
  return ctx;
}

/** Zugriff auf Client + Config. */
export function useGlobalLogin(): GlobalLoginContextValue {
  return useCtx();
}

/** Live-Session des zentralen Logins (`loading` bis zum ersten Auflösen). */
export function useSession(): { session: Session | null; loading: boolean } {
  const { client } = useCtx();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void client.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = client.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [client]);

  return { session, loading };
}

/** Guard-Zustand: noch ladend / freigegeben / abgelehnt. */
export type GuardState = 'loading' | 'ok' | 'denied';

/**
 * Verlangt eine gültige Session + App-Zugang. Ohne Session → Redirect zum
 * zentralen Login. Mit Session, aber ohne Freischaltung → `denied`.
 *
 * @param requiredRole Optional: konkrete Rolle, die zusätzlich erfüllt sein muss.
 */
export function useRequireAccess(requiredRole?: string): GuardState {
  const { config } = useCtx();
  const { session, loading } = useSession();

  useEffect(() => {
    if (!loading && !session) redirectToLogin(config);
  }, [loading, session, config]);

  if (loading) return 'loading';
  if (!session) return 'loading'; // Redirect läuft
  if (!hasAppAccess(session, config.appSlug)) return 'denied';
  if (requiredRole && roleForApp(session, config.appSlug) !== requiredRole) return 'denied';
  return 'ok';
}

/** Status eines Login-Popup-Vorgangs. */
export type LoginPopupStatus = 'idle' | 'pending' | 'success' | 'error';

/**
 * Öffnet den zentralen Login als Popup — für Flows, die inline nicht möglich
 * sind (v. a. Google-OAuth im eingebetteten Kontext). Nach dem Auflösen wird die
 * (geteilte) Session per `getSession()` nachgeprüft, da das Cookie subdomain-weit
 * gilt; `onAuthStateChange` aktualisiert {@link useSession} ohnehin automatisch.
 *
 * Unmount-sicher: jeder `open()`-Aufruf bekommt einen eigenen `AbortController`;
 * beim Unmount (oder beim Start eines neuen `open()`) wird der laufende Vorgang
 * abgebrochen — Listener/Timer im {@link loginPopup} werden abgeräumt, das
 * Popup-Fenster geschlossen und es läuft KEIN `setStatus` mehr auf einer bereits
 * demontierten Komponente (M2).
 *
 * @returns `open(returnTo?)` startet das Popup und resolved `true` bei Erfolg;
 *   `status` spiegelt den Fortschritt (`idle`→`pending`→`success`/`error`).
 */
export function useLoginPopup(): {
  open: (returnTo?: string) => Promise<boolean>;
  status: LoginPopupStatus;
} {
  const { client, config } = useCtx();
  const [status, setStatus] = useState<LoginPopupStatus>('idle');
  const mountedRef = useRef(true);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Laufenden Popup-Vorgang beim Unmount abbrechen (schließt das Fenster,
      // räumt Listener/Timer ab und verhindert setStatus nach Unmount).
      controllerRef.current?.abort();
    };
  }, []);

  const open = useCallback(
    async (returnTo?: string): Promise<boolean> => {
      // Einen evtl. noch laufenden Vorgang zuerst abbrechen, dann neu starten.
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      if (mountedRef.current) setStatus('pending');

      const acknowledged = await loginPopup(config, returnTo, { signal: controller.signal });
      if (controller.signal.aborted || !mountedRef.current) return false;

      // Cookie ist subdomain-weit geteilt → Session unabhängig von der
      // Popup-Nachricht nachprüfen (deckt auch Fälle ohne postMessage ab).
      const { data } = await client.auth.getSession();
      if (controller.signal.aborted || !mountedRef.current) return false;

      const signedIn = acknowledged || Boolean(data.session);
      setStatus(signedIn ? 'success' : 'error');
      return signedIn;
    },
    [client, config],
  );

  return { open, status };
}

/** Liest die `sb-*`-Cookie-Namen des aktuellen Hosts (Diagnose, kein Wert-Leak). */
function readSbCookieNames(): string[] {
  if (typeof document === 'undefined') return [];
  return document.cookie
    .split('; ')
    .map((c) => c.split('=')[0] ?? '')
    .filter((n) => n.startsWith('sb-'));
}

/** Eine Kennzeile im Diagnose-Panel. */
function Row(props: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col gap-0.5 border-b border-black/10 py-2 last:border-0 sm:flex-row sm:items-baseline sm:gap-3">
      <span className="min-w-44 shrink-0 text-xs font-medium uppercase tracking-wide opacity-60">
        {props.label}
      </span>
      <span className="font-mono text-sm break-all">{props.children}</span>
    </div>
  );
}

/**
 * Router-agnostisches Diagnose-Panel für den zentralen Login. Zeigt für die
 * AKTUELLE App/Subdomain, was ihr Client real sieht: Session-Status, Claims
 * (email, app_access, exp, username), die `sb-*`-Cookies des Hosts und die
 * aufgelöste SDK-Config (Projekt-URL, Slug, Cookie-Domain). Macht Cross-Subdomain-
 * SSO-Probleme sofort sichtbar (z. B. „Login sieht Session, Admin nicht").
 *
 * Bewusst ohne UI-Kit-Abhängigkeit (nur Tailwind-Utilities), damit es in jede App
 * passt. NICHT für Endnutzer gedacht — reine Test-/Debug-Oberfläche.
 *
 * @param props.appLabel Klartext-Name der App/Subdomain (z. B. `admin.gmolike.me`).
 * @returns Das gerenderte Status-Panel.
 */
export function AuthStatusPanel(props: { appLabel: string }): ReactNode {
  const { client, config } = useCtx();
  const { session, loading } = useSession();
  const [cookieNames, setCookieNames] = useState<string[]>([]);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    setCookieNames(readSbCookieNames());
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [session]);

  const claims = readClaims(session);
  const status = loading ? 'lädt …' : session ? 'Session aktiv' : 'keine Session';
  const statusClass = loading
    ? 'bg-amber-500/15 text-amber-700'
    : session
      ? 'bg-emerald-500/15 text-emerald-700'
      : 'bg-red-500/15 text-red-700';
  const expiresIn = claims?.exp ? claims.exp - now : null;

  async function signOut(): Promise<void> {
    await client.auth.signOut();
    window.location.reload();
  }

  return (
    <div className="mx-auto w-full max-w-2xl p-6 font-sans text-foreground">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Auth-Status · {props.appLabel}</h1>
        <span className={`rounded-md px-2 py-1 text-xs font-medium ${statusClass}`}>{status}</span>
      </div>

      <div className="rounded-xl border p-4">
        <Row label="Host">{typeof window !== 'undefined' ? window.location.host : '—'}</Row>
        <Row label="Projekt-URL (Config)">{config.supabaseUrl}</Row>
        <Row label="App-Slug (Config)">{config.appSlug}</Row>
        <Row label="Cookie-Domain (Config)">
          {config.cookieDomain ?? `${DEFAULT_COOKIE_DOMAIN} (default)`}
        </Row>
        <Row label="sb-Cookies (Host)">
          {cookieNames.length ? cookieNames.join(', ') : '— keine —'}
        </Row>
        <Row label="E-Mail (Claim)">{claims?.email ?? '—'}</Row>
        <Row label="Username (Claim)">{claims?.username ?? '—'}</Row>
        <Row label="app_access (Claim)">{claims ? JSON.stringify(claims.app_access) : '—'}</Row>
        <Row label="Token läuft ab in">{expiresIn != null ? `${expiresIn}s` : '—'}</Row>
      </div>

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-black/5"
        >
          Neu laden
        </button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-black/5"
        >
          Abmelden
        </button>
      </div>
    </div>
  );
}

// Einbettbare, Tailwind-only Auth-Dialoge (React) werden ausschließlich über den
// `./react`-Entry exponiert — der `.`-Entry (`index.ts`) bleibt frei von
// React-KOMPONENTEN. (Er zieht seit der Rechtstext-Datenschicht `queryOptions`
// aus `@tanstack/react-query`, das darum eine peerDependency ist.)
export { Dialog, DialogButton } from './dialog';
export type { DialogProps, DialogButtonProps } from './dialog';
export { RequestAccessDialog, useRequestAccess } from './request-access';
export type { RequestAccessDialogProps, UseRequestAccessResult } from './request-access';
export { LoginDialog } from './login-dialog';
export type { LoginDialogProps } from './login-dialog';
// Rechtsseiten (Impressum/Datenschutz/KI-Kennzeichnung) inkl. des sicheren
// Markdown-Renderers — letzterer bewusst mit-exportiert, damit eine App das
// aufgelöste Dokument nie über `dangerouslySetInnerHTML` selbst rendern muss.
export { LegalPage } from './legal-page';
export type { LegalPageProps } from './legal-page';
export { Markdown } from './markdown';
export type { MarkdownProps } from './markdown';
// Laufzeit-KI-Kennzeichnung (Ebene B der KI-Transparenz, Art. 50 KI-VO): der Hinweis
// steht an der Interaktion, nicht nur im Rechtstext. Merkmals-Quelle ist dieselbe
// `traits`-Zeile, die den Rechtstext schaltet — `useLegalTraits` liest sie aus derselben
// Query wie `<LegalPage category="ki">`.
export {
  AiDisclosureNotice,
  AiGeneratedBadge,
  AiDeepfakeNotice,
  useLegalTraits,
} from './ai-disclosure';
export type {
  AiDisclosureNoticeProps,
  AiGeneratedBadgeProps,
  AiDeepfakeNoticeProps,
  AiGeneratedKind,
  LegalTraitsState,
} from './ai-disclosure';
